import * as React from 'react';
import { TextField } from '@fluentui/react';
import { SavingState } from 'api/savingState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formatDecimal, getDecimalCount, parseDecimal } from 'utils/formatter';

const styles = mergeStyleSets({
    field: {
        textAlign: 'right',
    },
});

export interface FormDecimalFieldOptions {
    disabled?: boolean;
    fixed?: number;
    maxLength?: number;
    onChange?: (newValue: number) => void;
}

interface FormDecimalFieldProps {
    label?: string;
    placeholder?: string;
    value: number;
    onChange: (newValue: number) => void;
    savingState?: SavingState;
    options?: FormDecimalFieldOptions;
}

export const FormDecimalField: React.FC<FormDecimalFieldProps> = ({
    label,
    placeholder,
    value,
    onChange,
    savingState,
    options,
}) => {
    const fixed = options?.fixed !== undefined ? options.fixed : 2;

    const formatValue = React.useCallback(
        (newValue) => {
            return formatDecimal(newValue, Math.max(getDecimalCount(newValue), fixed));
        },
        [fixed],
    );

    const [textValue, setTextValue] = React.useState(() => {
        return formatValue(value);
    });

    React.useEffect(() => {
        setTextValue(formatValue(value));
    }, [value, formatValue]);

    return (
        <TextField
            inputClassName={styles.field}
            label={label}
            placeholder={placeholder}
            value={placeholder && textValue === formatValue(0) ? '' : textValue}
            type="text"
            disabled={savingState === SavingState.saving || options?.disabled}
            maxLength={options?.maxLength}
            onChange={(event, newValue) => {
                if (/^[0-9,-]*$/.test(newValue || '')) {
                    setTextValue(newValue || '');
                }
            }}
            onBlur={() => {
                const newValue = parseDecimal(textValue) || 0;
                setTextValue(formatValue(newValue));
                onChange(newValue);
            }}
        />
    );
};
